import { CSSProperties, useMemo } from 'react';
import { DataPackageValidationModeEnum } from '@vivli/features/studies/infrastructure/enum';
import { Color, Size } from '@vivli/shared/theme';
import { FieldHeaderComponent } from '@vivli/shared/components';
import { IDataPackageFileDescriptor } from '@vivli/shared/features/data-package/infrastructure/interface';
import { isFileUploadComplete, isFileUploadFailed, isFileUploadInProgress } from '@vivli/shared/infrastructure/hook';

const containerStyle: CSSProperties = {
    color: Color.ERROR_RED,
    marginBottom: Size.PADDING,
    paddingBottom: 5,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    border: '1px solid red'
};

const accessoryStyle: CSSProperties = {
    width: '100%',
    display: 'block',
    whiteSpace: 'normal'
};

const anyStyle: CSSProperties = {
    fontWeight: Size.FontWeight.Bold,
    color: Color.ERROR_RED
};

const ANY_MESSAGE =
    'YOU MUST SUPPLY ONE OF THE FOLLOWING REQUIRED FILE TYPES AS DISTINCT FILES, AND FOR EACH FILE, YOU MUST SPECIFY THE FILE TYPE. If a file type is unavailable, please provide a file with a note of explanation';
const ALL_MESSAGE =
    'YOU MUST SUPPLY ALL REQUIRED FILE TYPES AS DISTINCT FILES, AND FOR EACH FILE, YOU MUST SPECIFY THE FILE TYPE. If a file type is unavailable, please provide a file with a note of explanation';
const INCOMPLETE_MESSAGE =
    'THE FOLLOWING REQUIRED FILE TYPES HAVE A FILE THAT DID NOT FINISH UPLOADING. Retry the upload, or remove the file and upload it again';
// Files that did not finish but carry no required type. They block Submit just the same — the package
// is not fully uploaded — but nothing above would mention them, because both lists above are keyed on
// required types. A failed file left on the default 'Unknown' type is the common case. (DEV-746)
const BLOCKING_FILES_MESSAGE = 'THE FOLLOWING FILES DID NOT FINISH UPLOADING. Retry each one, or remove it, before submitting';

interface DataPackageValidationMessageComponentProps {
    validationMode: DataPackageValidationModeEnum;
    requiredTypes?: string[];
    requiredTypesForDisplay?: string[];
    files: IDataPackageFileDescriptor[];
}

export const DataPackageValidationMessageComponent = ({
    validationMode,
    requiredTypes,
    requiredTypesForDisplay,
    files
}: DataPackageValidationMessageComponentProps) => {
    const isAnyMode = validationMode === DataPackageValidationModeEnum.Any;

    // Split the unsatisfied required types into the two reasons they can be unsatisfied, so a
    // disabled Submit button always has an explanation. A type with a file that failed or never
    // finished uploading is not "missing" — telling the user to supply it while a Failed row for
    // that very type is on screen reads as a contradiction (DEV-699).
    // blockingFiles is deliberately NOT keyed on required types: it names any file that has not
    // finished, so the banner can explain a disabled Submit even when every required type is
    // satisfied. Files that DO carry a required type are excluded, because incompleteTypes already
    // reports those and naming them twice is just noise. (DEV-746)
    const { missingTypes, incompleteTypes, blockingFiles } = useMemo(() => {
        const missing: string[] = [];
        const incomplete: string[] = [];

        const isUnfinished = (fd: IDataPackageFileDescriptor) => isFileUploadFailed(fd) || isFileUploadInProgress(fd);
        const blocking = (files || []).filter((fd) => isUnfinished(fd) && !requiredTypes?.includes(fd.type)).map((fd) => fd.name);

        if (!requiredTypes) {
            return { missingTypes: missing, incompleteTypes: incomplete, blockingFiles: blocking };
        }

        // Index-paired with requiredTypes: requiredTypesForDisplay[i] is the display name of
        // requiredTypes[i]. It is populated from config in an effect, so fall back to the raw
        // type name on the first render rather than showing an unexplained empty banner.
        requiredTypes.forEach((type, i) => {
            const displayName = requiredTypesForDisplay?.[i] ?? type;
            const filesOfType = (files || []).filter((fd) => fd.type === type);

            if (filesOfType.length === 0) {
                missing.push(displayName);
            } else if (!filesOfType.some(isFileUploadComplete)) {
                incomplete.push(displayName);
            }
        });

        return { missingTypes: missing, incompleteTypes: incomplete, blockingFiles: blocking };
    }, [requiredTypes, requiredTypesForDisplay, files]);

    // Nothing to report — don't render an empty red box.
    if (missingTypes.length === 0 && incompleteTypes.length === 0 && blockingFiles.length === 0) {
        return null;
    }

    return (
        <div style={containerStyle}>
            {missingTypes.length > 0 && (
                <>
                    <FieldHeaderComponent
                        style={isAnyMode && anyStyle}
                        accessoryStyle={accessoryStyle}
                        title={isAnyMode ? ANY_MESSAGE : ALL_MESSAGE}
                    />

                    <ul>
                        {missingTypes.map((type, i) => (
                            <li key={i}>{type}</li>
                        ))}
                    </ul>
                </>
            )}

            {incompleteTypes.length > 0 && (
                <>
                    <FieldHeaderComponent style={isAnyMode && anyStyle} accessoryStyle={accessoryStyle} title={INCOMPLETE_MESSAGE} />

                    <ul>
                        {incompleteTypes.map((type, i) => (
                            <li key={i}>{type}</li>
                        ))}
                    </ul>
                </>
            )}

            {blockingFiles.length > 0 && (
                <>
                    <FieldHeaderComponent style={isAnyMode && anyStyle} accessoryStyle={accessoryStyle} title={BLOCKING_FILES_MESSAGE} />

                    <ul>
                        {blockingFiles.map((name, i) => (
                            <li key={i}>{name}</li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
};
